import { useEffect, useRef } from "react";
import { useAktion } from "@/lib/useAktion";
import { trackAktion } from "@/lib/analytics";

/**
 * Aktions-Sektion — wird zwischen Hero und nächster Bestandssektion
 * gerendert, wenn die Referenzaktion aktiv ist. Editorial-konsistent
 * mit Sand-Rahmen und Serif-Headline. Impressions werden einmalig
 * beim ersten In-View-Kommen der Sektion getrackt.
 */
export default function AktionSection() {
    const aktion = useAktion();
    const sectionRef = useRef<HTMLElement | null>(null);
    const shownFired = useRef(false);

    useEffect(() => {
        if (!aktion.active) return;
        const el = sectionRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                if (shownFired.current) return;
                for (const e of entries) {
                    if (e.isIntersecting) {
                        shownFired.current = true;
                        trackAktion("section_shown");
                        io.disconnect();
                        break;
                    }
                }
            },
            { threshold: 0.4 },
        );
        io.observe(el);
        return () => io.disconnect();
    }, [aktion.active]);

    if (!aktion.active) return null;

    const onCta = () => {
        trackAktion("section_cta_clicked");
        const el = document.getElementById("kontakt");
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            // Kontakt-Formular Preselection setzen via Custom-Event —
            // wird vom ContactForm gelesen und als Hidden-`source`-Feld
            // im Submit mitgeschickt.
            window.dispatchEvent(new CustomEvent("hf:aktion-cta-focused"));
        }
    };

    return (
        <section
            id="aktion-section"
            ref={sectionRef}
            className="py-16 md:py-24"
            style={{ background: "hsl(var(--hf-surface))" }}
            data-testid="aktion-section"
        >
            <div className="max-w-4xl mx-auto px-6">
                <div
                    className="rounded-sm p-8 md:p-12"
                    style={{
                        background: "#FFFDF9",
                        border: "1px solid #D8CDBF",
                        boxShadow: "0 12px 32px -16px rgba(30, 25, 20, 0.15)",
                    }}
                >
                    <div
                        className="uppercase text-[10px] tracking-[0.24em] mb-4 flex items-center gap-3 flex-wrap"
                        style={{ color: "#A78150", fontWeight: 600 }}
                    >
                        <span>{aktion.section_eyebrow}</span>
                        {aktion.slots_visible && (
                            <SlotsBadge
                                remaining={aktion.slots_remaining}
                                total={aktion.slots_total}
                                template={aktion.slots_label_template}
                                shimmer={aktion.slots_shimmer_intensity}
                            />
                        )}
                    </div>
                    <h2
                        className="font-display text-3xl md:text-5xl leading-tight"
                        style={{ color: "hsl(var(--hf-ink))" }}
                    >
                        {aktion.section_headline}
                    </h2>
                    <p
                        className="mt-6 text-base leading-relaxed max-w-2xl"
                        style={{ color: "hsl(var(--hf-muted))" }}
                    >
                        {aktion.section_body}
                    </p>
                    <div
                        className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4"
                    >
                        <div
                            className="px-4 py-3 rounded-sm"
                            style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))" }}
                        >
                            <div className="uppercase text-[9px] tracking-[0.2em] text-hf-muted">Regulär</div>
                            <div
                                className="font-display text-lg mt-1"
                                style={{ color: "hsl(var(--hf-ink))" }}
                                data-testid="aktion-price-regular"
                            >
                                {aktion.price_regular_text}
                            </div>
                        </div>
                        <div
                            className="px-4 py-3 rounded-sm"
                            style={{
                                background: "hsl(var(--hf-gold-soft) / 0.5)",
                                border: "1px solid #A78150",
                            }}
                        >
                            <div className="uppercase text-[9px] tracking-[0.2em]" style={{ color: "#A78150" }}>Referenzaktion</div>
                            <div
                                className="font-display text-lg mt-1"
                                style={{ color: "hsl(var(--hf-ink))" }}
                                data-testid="aktion-price-advantage"
                            >
                                {aktion.price_advantage_text}
                            </div>
                        </div>
                    </div>
                    <div className="mt-8">
                        <button
                            type="button"
                            onClick={onCta}
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-sm md:text-base transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A78150]"
                            style={{ background: "#A78150", color: "#FFFDF9", fontWeight: 500, letterSpacing: "0.02em" }}
                            data-testid="aktion-section-cta"
                        >
                            {aktion.section_cta_label}
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}

/** Verfügbarkeits-Badge „X von Y noch frei" — dezent, editorial.
 *  Bei `remaining <= 0` sanfter Fallback „Aktion vollständig belegt".
 *  Optionaler pulsierender Voll-Gold-Effekt (off/subtle/medium/strong).
 *  Bei Sold-Out immer aus (grau, kein Puls). */
function SlotsBadge({
    remaining, total, template, shimmer = "medium",
}: {
    remaining: number;
    total: number;
    template: string;
    shimmer?: "off" | "subtle" | "medium" | "strong";
}) {
    const r = Math.max(0, Math.floor(remaining));
    const t = Math.max(0, Math.floor(total));
    const soldOut = r <= 0;
    const label = soldOut
        ? "Aktion vollständig belegt"
        : (template || "{remaining} von {total} noch frei")
            .replace(/\{remaining\}/g, String(r))
            .replace(/\{total\}/g, String(t));
    const pulseActive = !soldOut && shimmer !== "off";
    const pulseClass = pulseActive
        ? ` hf-slots-pulse hf-slots-pulse-${shimmer}`
        : "";
    return (
        <span
            className={`inline-flex items-center gap-1 text-[10px] tracking-[0.14em] normal-case px-2 py-0.5 rounded-sm${pulseClass}`}
            style={
                soldOut
                    ? {
                        background: "hsl(var(--hf-surface))",
                        border: "1px solid hsl(var(--hf-line))",
                        color: "hsl(var(--hf-muted))",
                        fontWeight: 500,
                    }
                    : pulseActive
                        ? { fontWeight: 600 }
                        : {
                            background: "#FDF7EC",
                            border: "1px solid #E4CFA4",
                            color: "#8A6534",
                            fontWeight: 500,
                        }
            }
            data-testid="aktion-slots-badge"
            data-shimmer={pulseActive ? shimmer : "off"}
        >
            <span
                aria-hidden
                className="inline-block rounded-full"
                style={{
                    width: 6, height: 6,
                    background: soldOut
                        ? "hsl(var(--hf-muted))"
                        : pulseActive
                            ? "#FFF3D6"
                            : "#C48B34",
                    opacity: soldOut ? 0.6 : 1,
                    boxShadow: pulseActive
                        ? "0 0 4px rgba(255, 220, 140, 0.9)"
                        : undefined,
                }}
            />
            {label}
        </span>
    );
}
